import React, { FC, ReactElement, useContext } from 'react'
import { LanguageContext } from '../../Common/Contexts'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import Fieldset from '../../Common/Components/Fieldset'
import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import { UnreturnedCallsFilter } from '../../../../i18n'
import P from '../../Common/Components/P'
import FormRow from '../../Common/Components/FormRow'
import Checkbox from '../../Common/Components/Checkbox'

type Props = {
    includeEscapedToCallsFilter: boolean
    setIncludeEscapedToCallsFilter: (checked: boolean) => void
    includeDivertedOverflowedToCallsFilter: boolean
    setIncludeDivertedOverflowedToCallsFiltered: (checked: boolean) => void
    includeTransferredToCallsFilter: boolean
    setIncludeTransferredToCallsFilter: (checked: boolean) => void
    includeAllOtherCallsFilter: boolean
    setIncludeAllOtherCallsFilter: (checked: boolean) => void
}

const UnreturnedCallsFiltering: FC<Props> = ({
    includeEscapedToCallsFilter,
    setIncludeEscapedToCallsFilter,
    includeDivertedOverflowedToCallsFilter,
    setIncludeDivertedOverflowedToCallsFiltered,
    includeTransferredToCallsFilter,
    setIncludeTransferredToCallsFilter,
    includeAllOtherCallsFilter,
    setIncludeAllOtherCallsFilter
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(UnreturnedCallsFilter.NAME)

    const isUnreturnedCallsFilteringChecked: boolean =
        includeEscapedToCallsFilter ||
        includeDivertedOverflowedToCallsFilter ||
        includeTransferredToCallsFilter ||
        includeAllOtherCallsFilter

    const handleCheckboxChange = (value: boolean, setter: (checked: boolean) => void): void => {
        if (!value && isUnreturnedCallsFilteringChecked) {
            const remainingChecked: number =
                (includeEscapedToCallsFilter ? 1 : 0) +
                (includeDivertedOverflowedToCallsFilter ? 1 : 0) +
                (includeTransferredToCallsFilter ? 1 : 0) +
                (includeAllOtherCallsFilter ? 1 : 0)

            if (remainingChecked <= 1) return
        }
        setter(value)
    }

    return (
        <Fieldset className={'unreturned-calls'}>
            <Heading level={HeadingLevel.ONE}>
                {language.get(UnreturnedCallsFilter.UnreturnedCallsFilterSectionTitle)}
            </Heading>
            <P>{language.get(UnreturnedCallsFilter.UnreturnedCallsFilterSectionDesc)}</P>
            <FormRow isInline className={'include-escaped-to-calls'}>
                <Checkbox
                    className='left-aligned-checkbox'
                    labelLeft={language.get(UnreturnedCallsFilter.UnreturnedCallsFilter_IncludeEscapedLabel)}
                    onChange={(value: boolean): void => handleCheckboxChange(value, setIncludeEscapedToCallsFilter)}
                    isChecked={includeEscapedToCallsFilter}
                />
            </FormRow>
            <FormRow isInline className={'include-escaped'}>
                <Checkbox
                    className='left-aligned-checkbox'
                    labelLeft={language.get(UnreturnedCallsFilter.UnreturnedCallsFilter_IncludeDivertedOverflowedLabel)}
                    onChange={(value: boolean): void =>
                        handleCheckboxChange(value, setIncludeDivertedOverflowedToCallsFiltered)
                    }
                    isChecked={includeDivertedOverflowedToCallsFilter}
                />
            </FormRow>
            <FormRow isInline className={'include-escaped'}>
                <Checkbox
                    className='left-aligned-checkbox'
                    labelLeft={language.get(UnreturnedCallsFilter.UnreturnedCallsFilter_IncludeTransferredLabel)}
                    onChange={(value: boolean): void => handleCheckboxChange(value, setIncludeTransferredToCallsFilter)}
                    isChecked={includeTransferredToCallsFilter}
                />
            </FormRow>
            <FormRow isInline className={'include-escaped'}>
                <Checkbox
                    className='left-aligned-checkbox'
                    labelLeft={language.get(UnreturnedCallsFilter.UnreturnedCallsFilter_IncludeAllOtherLabel)}
                    onChange={(value: boolean): void => handleCheckboxChange(value, setIncludeAllOtherCallsFilter)}
                    isChecked={includeAllOtherCallsFilter}
                />
            </FormRow>
        </Fieldset>
    )
}

export default React.memo(UnreturnedCallsFiltering)
